import React from "react";
import {
  fmtUptime,
  fmtNet,
  fmtGB,
  svStatus,
} from "../../utils/format";

export default function ControlCard({ sv, actions }) {
  const code = sv.ServerCode;
  const m = sv.metrics || {};
  const cpu = m.cpuUsage || 0;
  const ram = m.ramPct || 0;
  const disk = m.diskPct || 0;
  const st = svStatus(sv); // online | warn | offline

  const isOffline = st === "offline";

  // Hàm lấy màu sắc động
  const getStatColor = (val) => {
    if (isOffline) return "var(--faint)";
    if (val >= 85) return "var(--red)";
    if (val >= 70) return "var(--amber)";
    return "var(--green)";
  };

  const cpuC = getStatColor(cpu);
  const ramC = getStatColor(ram);
  const diskC = getStatColor(disk);

  // Uptime badge style
  const uptimeBadgeBg = st === "offline" ? "var(--red-soft)" : st === "warn" ? "var(--amber-soft)" : "var(--green-soft)";
  const uptimeBadgeColor = st === "offline" ? "var(--red)" : st === "warn" ? "var(--amber)" : "var(--green)";
  const uptimeText = isOffline ? "OFFLINE" : `UP ${fmtUptime(m.uptime)}`;

  const rxSpeed = isOffline ? "0.0 B/s" : `${fmtNet(m.netRx)}/s`;
  const txSpeed = isOffline ? "0.0 B/s" : `${fmtNet(m.netTx)}/s`;

  // Lấy danh sách apps đang chạy
  const runningApps = (sv.apps || []).filter(a => a.isRunning).slice(0, 2);

  return (
    <div 
      style={{
        borderRadius: "16px",
        background: "var(--surface)",
        border: `1px solid ${st === "warn" ? "var(--amber-soft)" : "var(--border)"}`,
        overflow: "hidden",
        boxShadow: "var(--shadow)",
        position: "relative",
        opacity: isOffline ? 0.68 : 1,
      }}
      className="ctrl-detail-card"
    >
      {/* Warning Strip */}
      {st === "warn" && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "linear-gradient(90deg, var(--amber), transparent 75%)",
        }}></div>
      )}

      {/* Card Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "15px 18px",
        borderBottom: "1px solid var(--border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <span style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: isOffline ? "var(--faint)" : st === "warn" ? "var(--amber)" : "var(--green)",
            boxShadow: isOffline ? "none" : `0 0 0 4px ${st === "warn" ? "var(--amber-soft)" : "var(--green-soft)"}`,
            animation: st === "warn" ? "warnBlink 2s infinite" : "none",
          }}></span>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: isOffline ? "var(--dim)" : "var(--text)" }}>{code}</div>
            <div style={{ fontSize: "11.5px", color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
              {sv.IP || "—"} · {sv.OS || "Windows Server 2022"}
            </div>
          </div>
        </div>
        <span style={{
          fontFamily: "var(--font-mono)",
          fontSize: "11px",
          fontWeight: 600,
          color: uptimeBadgeColor,
          padding: "4px 10px",
          borderRadius: "8px",
          background: uptimeBadgeBg,
        }}>
          {uptimeText}
        </span>
      </div>

      {/* Buttons Actions Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "8px",
        padding: "16px 18px",
      }}>
        {/* PowerShell */}
        <button
          onClick={() => !isOffline && actions.shell(code)}
          disabled={isOffline}
          style={actionBtnStyle("var(--primary)", isOffline)}
          className="ctrl-btn-ps"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 17 10 11 4 5"></polyline>
            <line x1="12" y1="19" x2="20" y2="19"></line>
          </svg>
          PowerShell
        </button>

        {/* Password */}
        <button
          onClick={() => !isOffline && actions.passwd(code)}
          disabled={isOffline}
          style={actionBtnStyle("var(--blue)", isOffline)}
          className="ctrl-btn-pw"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="7.5" cy="15.5" r="4.5"></circle>
            <path d="m10.7 12.3 8.3-8.3 2 2-2 2 1.5 1.5-2.5 2.5-1.5-1.5-1.5 1.5z"></path>
          </svg>
          Password
        </button>

        {/* Restart */}
        <button
          onClick={() => !isOffline && actions.restart(code)}
          disabled={isOffline}
          style={actionBtnStyle("var(--amber)", isOffline)}
          className="ctrl-btn-restart"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2v6h-6"></path>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
            <path d="M3 22v-6h6"></path>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
          </svg>
          Restart
        </button>

        {/* Shutdown */}
        <button
          onClick={() => !isOffline && actions.shutdown(code)}
          disabled={isOffline}
          style={actionBtnStyle("var(--red)", isOffline)}
          className="ctrl-btn-shutdown"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
            <line x1="12" y1="2" x2="12" y2="12"></line>
          </svg>
          Shutdown
        </button>
      </div>

      {/* Stats Table-like Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "1px",
        background: "var(--border)",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}>
        {/* CPU */}
        <div style={{ background: "var(--surface)", padding: "12px 16px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".6px", color: "var(--faint)", textTransform: "uppercase" }}>CPU</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "21px", fontWeight: 700, color: cpuC, marginTop: "3px" }}>
            {isOffline ? "—" : `${Math.round(cpu)}%`}
          </div>
          <div style={{ fontSize: "11px", color: "var(--faint)", marginTop: "1px" }}>current</div>
        </div>

        {/* RAM */}
        <div style={{ background: "var(--surface)", padding: "12px 16px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".6px", color: "var(--faint)", textTransform: "uppercase" }}>RAM</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "21px", fontWeight: 700, color: ramC, marginTop: "3px" }}>
            {isOffline ? "—" : `${Math.round(ram)}%`}
          </div>
          <div style={{ fontSize: "11px", color: "var(--faint)", marginTop: "1px" }}>
            {m.ramTotal ? `${Math.round(m.ramTotal)} GB` : "—"}
          </div>
        </div>

        {/* DISK */}
        <div style={{ background: "var(--surface)", padding: "12px 16px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".6px", color: "var(--faint)", textTransform: "uppercase" }}>DISK</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "21px", fontWeight: 700, color: diskC, marginTop: "3px" }}>
            {isOffline ? "—" : `${Math.round(disk)}%`}
          </div>
          <div style={{ fontSize: "11px", color: "var(--faint)", marginTop: "1px" }}>
            {m.diskTotal ? fmtGB(m.diskTotal) : "—"}
          </div>
        </div>
      </div>

      {/* Network Speeds and Active Apps */}
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M19 12l-7 7-7-7"></path>
          </svg>
          <div>
            <div style={{ fontSize: "10px", color: "var(--faint)", textTransform: "uppercase", fontWeight: 600 }}>RX</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "13px", fontWeight: 600, color: "var(--blue)" }}>{rxSpeed}</div>
          </div>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5M5 12l7-7 7 7"></path>
          </svg>
          <div>
            <div style={{ fontSize: "10px", color: "var(--faint)", textTransform: "uppercase", fontWeight: 600 }}>TX</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "13px", fontWeight: 600, color: "var(--green)" }}>{txSpeed}</div>
          </div>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
          {isOffline ? (
            <span style={{ fontSize: "11px", fontFamily: "var(--font-mono)", color: "var(--faint)" }}>Offline</span>
          ) : runningApps.length === 0 ? (
            <span style={{ fontSize: "11px", fontFamily: "var(--font-mono)", color: "var(--faint)" }}>No active app</span>
          ) : (
            runningApps.map((a, i) => (
              <span key={i} style={{
                fontSize: "11px",
                fontFamily: "var(--font-mono)",
                color: "var(--green)",
                padding: "3px 9px",
                borderRadius: "7px",
                background: "var(--green-soft)",
              }}>
                {a.appName} ●
              </span>
            ))
          )}
        </div>
      </div>
      
      <style>{`
        .ctrl-btn-ps:hover:not(:disabled) { border-color: var(--primary) !important; color: var(--primary) !important; }
        .ctrl-btn-pw:hover:not(:disabled) { border-color: var(--blue) !important; color: var(--blue) !important; }
        .ctrl-btn-restart:hover:not(:disabled) { border-color: var(--amber) !important; color: var(--amber) !important; }
        .ctrl-btn-shutdown:hover:not(:disabled) { border-color: var(--red) !important; color: var(--red) !important; }
      `}</style>
    </div>
  );
}

const actionBtnStyle = (hoverColor, isOffline) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "6px",
  padding: "12px 4px",
  borderRadius: "11px",
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  color: isOffline ? "var(--faint)" : "var(--text)",
  fontSize: "11.5px",
  fontWeight: 600,
  cursor: isOffline ? "not-allowed" : "pointer",
  transition: "all 0.2s ease",
});
